<template>
  <div class="card p-p-4">
    <div class="p-d-flex p-jc-between p-ai-center p-mb-3">
      <h2 class="p-m-0">Stock</h2>
      <Button label="Novo Stock" icon="pi pi-plus" class="p-button-sm" @click="openModal" v-if="can('manageStock')"/>
    </div>

    <DataTable
      :value="stocks"
      :filters.sync="filters"
      :paginator="true"
      :rows="perPage"
      :totalRecords="totalRecords"
      :loading="loading"
      :first="(currentPage - 1) * perPage"
      lazy
      @filter="onFilter"
      @page="onPage"
      responsiveLayout="scroll"
      filterDisplay="row"
      class="p-datatable-sm"
      :rowKey="(row, index) => `${row.product?.ststamp || 'unknown'}-${index}`"
      @sort="onSort"
      :sortOrder="sortOrder"
      :sortField="sortField"
      >
      <template #empty> Nenhum stock encontrado. </template>
      <template #loading> A carregar... </template>

      <Column field="product_ref" header="Referência" filterField="product_ref" sortable>
        <template #body="{ data }">
          {{ data.product?.ref || '-' }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model="filterModel.value" class="p-column-filter" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="product_design" header="Designação" filterField="product_design" sortable>
        <template #body="{ data }">
          {{ data.product?.design || '-' }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model="filterModel.value" class="p-column-filter" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="product_familia" header="Família" filterField="product_familia" sortable>
        <template #body="{ data }">
          {{ data.product?.familia || '-' }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model="filterModel.value" class="p-column-filter" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="storage_name" header="Armazém" filterField="armazem">
        <template #body="{ data }">
          {{ data.storage?.nome || data.armazem || '-' }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model="filterModel.value" class="p-column-filter" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="stock" header="Quantidade Atual" filterField="stock" sortable>
        <template #body="{ data }">
          {{ formatNumber(data.stock) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model.number="filterModel.value" class="p-column-filter" @input="filterCallback()"/>
        </template>
      </Column>

      <Column field="optimo" header="Stock Ótimo" filterField="optimo" sortable>
        <template #body="{ data }">
          {{ formatNumber(data.optimo) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model.number="filterModel.value" class="p-column-filter" @input="filterCallback()"/>
        </template>
      </Column>

      <Column field="stmin" header="Stock Mínimo" filterField="stmin" sortable>
        <template #body="{ data }">
          {{ formatNumber(data.stmin) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model.number="filterModel.value" class="p-column-filter" @input="filterCallback()"/>
        </template>
      </Column>

      <Column field="consumo" header="Consumo" filterField="consumo" sortable>
        <template #body="{ data }">
          {{ formatNumber(data.consumo) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model.number="filterModel.value" class="p-column-filter" @input="filterCallback()"/>
        </template>
      </Column>

      <Column field="pv" header="Preço Venda" filterField="pv" sortable>
        <template #body="{ data }">
          {{ formatNumber(data.pv) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText v-model.number="filterModel.value" class="p-column-filter" @input="filterCallback()"/>
        </template>
      </Column>

      <Column header="Ações" style="width: 100px" v-if="can('manageStock')">
        <template #body="slotProps">
          <Button icon="pi pi-pencil" class="p-button-sm p-button-text" @click="openModal(slotProps.data)" />
        </template>
      </Column>
    </DataTable>

  <StockModal
    v-if="showModal"
    :visible.sync="showModal"
    :data="form"
    :storages="storages"
    @close="closeModal"
    @saved="handleStockSaved"
  />
  </div>
</template>

<script>
import StockService from "@/services/stock.service";
import { FilterMatchMode } from "primevue/api";
import StockModal from '@/components/stock/StockModal.vue';

export default {
  name: "StockList",
  components: {
    StockModal,
  },
  data() {
    return {
      loading: false,
      stocks: [],
      totalRecords: 0,
      currentPage: 1,
      perPage: 10,
      showModal: false,
      storages: [],
      sortField: null,
      sortOrder: null,
      form: {
        id: null,
        product_reference: '',
        storage_id: '',
        current_quantity: 0,
        optimal_quantity: 0,
        minimum_quantity: 0,
        consumption: 0,
        selling_price: 0,
      },
      filters: {
        product_ref: { value: null, matchMode: FilterMatchMode.CONTAINS },
        product_design: { value: null, matchMode: FilterMatchMode.CONTAINS },
        product_familia: { value: null, matchMode: FilterMatchMode.CONTAINS },
        armazem: { value: null, matchMode: FilterMatchMode.CONTAINS },
        stock: { value: null, matchMode: FilterMatchMode.EQUALS },
        optimo: { value: null, matchMode: FilterMatchMode.EQUALS },
        stmin: { value: null, matchMode: FilterMatchMode.EQUALS },
        consumo: { value: null, matchMode: FilterMatchMode.EQUALS },
        pv: { value: null, matchMode: FilterMatchMode.EQUALS },
      }
    };
  },
  mounted() {
    this.loadStocks();
    this.loadStorages();
  },
  methods: {
    async loadStocks() {
      this.loading = true;

      const params = {
        page: this.currentPage,
        per_page: this.perPage,
      };

      for (const key in this.filters) {
        if (this.filters[key].value) {
          params[key] = this.filters[key].value;
        }
      }

      if (this.sortField) {
        const fieldMap = {
          product_ref: 'product.ref',
          product_design: 'product.design',
          product_familia: 'product.familia',
          stock: 'stock',
          optimo: 'optimo',
          stmin: 'stmin',
          consumo: 'consumo',
          pv: 'pv'
        };

        params.sort_by = fieldMap[this.sortField] || this.sortField;
        params.sort_order = this.sortOrder === 1 ? 'asc' : 'desc';
      }

      try {
        const response = await StockService.getAll(params);
        this.stocks = response.data;
        this.totalRecords = response.total;
      } finally {
        this.loading = false;
      }
    },

    async loadStorages() {
      const response = await StockService.getStorages();
      this.storages = response.data;
    },

    onFilter() {
      this.currentPage = 1;
      this.loadStocks();
    },

    onPage(event) {
      this.currentPage = event.page + 1;
      this.loadStocks();
    },

    onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      this.loadStocks();
    },


    openModal(data = null) {
      if (data) {
        this.form = {
          id: data.sastamp,
          product_reference: data.product?.ref?.trim() || data.ref?.trim() || '',
          storage_id: data.armazem || '',
          current_quantity: Number(data.stock) || 0,
          optimal_quantity: Number(data.optimo) || 0,
          minimum_quantity: Number(data.stmin) || 0,
          consumption: Number(data.consumo) || 0,
          selling_price: Number(data.pv) || 0,
        };
      } else {
        this.resetForm();
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },

    resetForm() {
      this.form = {
        product_reference: '',
        storage_id: '',
        current_quantity: 0,
        optimal_quantity: 0,
        minimum_quantity: 0,
        consumption: 0,
        selling_price: 0,
      };
    },

    async saveStock() {
      try {
        const payload = {
          product_reference: this.form.product_reference,
          storage_id: this.form.storage_id,
          current_quantity: this.form.current_quantity,
          optimal_quantity: this.form.optimal_quantity,
          minimum_quantity: this.form.minimum_quantity,
          consumption: this.form.consumption,
          selling_price: this.form.selling_price,
        };

        if (this.form.id) {
          await StockService.update(this.form.id, payload);
        } else {
          await StockService.create(payload);
        }

        this.closeModal();
        this.loadStocks();
      } catch (error) {
        console.error('Erro ao guardar stock', error);
      }
    },

    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return '0.000';
      }
      return parseFloat(value).toFixed(2);
    },

    handleStockSaved() {
      this.closeModal();
      this.loadStocks();
    }

  }
};
</script>
